import { useCallback, useEffect, useState } from "react"

import api from "../../../api.js"
import { enqueue, flush, isOnline, OUTBOX_SYNCED_EVENT, pendingCount } from "../lib/outbox.js"

const RETRY_INTERVAL_MS = 20000

/**
 * Drives the offline outbox: reports what is queued and replays it whenever
 * connectivity looks likely to be back.
 */
export function useOutbox() {
  const [pending, setPending] = useState(0)
  const [online, setOnline] = useState(isOnline())
  const [dropped, setDropped] = useState([])

  const refreshCount = useCallback(async () => {
    setPending(await pendingCount())
  }, [])

  // The api client attaches .status for answered requests and leaves it unset
  // when the request never reached the server, which is exactly the distinction
  // the outbox needs to decide between dropping and retrying.
  const send = useCallback(
    (record) =>
      api.request({
        method: record.method || "POST",
        url: record.url,
        data: record.body ?? undefined,
      }),
    [],
  )

  const drain = useCallback(async () => {
    if (!isOnline()) return

    const result = await flush(send)
    if (result.dropped.length) {
      console.error("[useOutbox] dropped operations:", result.dropped)
      setDropped((prev) => [...prev, ...result.dropped])
    }
    setPending(result.remaining)

    // Something reached the server, so delivery state has moved on. Announce it
    // so the screens showing that state reload now instead of waiting out their
    // poll interval — the driver shouldn't have to refresh by hand to see a
    // delivery they already completed.
    if (result.sent > 0) {
      window.dispatchEvent(
        new CustomEvent(OUTBOX_SYNCED_EVENT, { detail: { sent: result.sent } }),
      )
    }
  }, [send])

  /** Send now if online, otherwise queue for later. Returns true if it went out. */
  const submit = useCallback(
    async ({ url, body, kind, dedupeKey, method = "POST" }) => {
      if (isOnline()) {
        try {
          await api.request({ method, url, data: body })
          return true
        } catch (err) {
          const status = err?.response?.status ?? err?.status ?? null
          // The server said no — that is an answer, not a connectivity problem,
          // and queueing it would just replay the same rejection later.
          if (status != null && status >= 400 && status < 500) throw err
        }
      }

      await enqueue({ method, url, body, kind, dedupeKey })
      await refreshCount()
      return false
    },
    [refreshCount],
  )

  useEffect(() => {
    refreshCount()

    // Drain on mount, not only on the online event. The completion that queued
    // this navigates away immediately, so the instance that enqueued is gone by
    // the time connectivity returns — whatever mounts next has to pick the queue
    // up, and the online event may already have fired and been missed.
    drain()

    const onOnline = () => {
      setOnline(true)
      drain()
    }
    const onOffline = () => setOnline(false)
    const onVisible = () => {
      if (document.visibilityState === "visible") drain()
    }

    window.addEventListener("online", onOnline)
    window.addEventListener("offline", onOffline)
    document.addEventListener("visibilitychange", onVisible)

    // navigator.onLine lies often enough (captive portals, flaky mobile data)
    // that a periodic attempt is more reliable than trusting the event alone.
    const interval = setInterval(drain, RETRY_INTERVAL_MS)

    return () => {
      window.removeEventListener("online", onOnline)
      window.removeEventListener("offline", onOffline)
      document.removeEventListener("visibilitychange", onVisible)
      clearInterval(interval)
    }
  }, [drain, refreshCount])

  const clearDropped = useCallback(() => setDropped([]), [])

  return { pending, online, dropped, submit, drain, refreshCount, clearDropped }
}

export default useOutbox
